import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField,
  Collapse, IconButton, Tooltip,
} from '@mui/material';
import {
  AccountBalanceWalletOutlined as BalanceIcon,
  CalendarMonthOutlined as HistoryIcon,
  PaymentsOutlined as PaymentIcon,
  TrendingDownRounded as RepayIcon,
  TrendingUpRounded as BorrowIcon,
  ExpandMoreRounded as ExpandIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import CustomerPaymentAccordionList from '../lists/CustomerPaymentAccordionList';
import LoanProjectExpenseList from '../lists/LoanProjectExpenseList';
import LoanLedgerTable from '../lists/LoanLedgerTable';
import { BORROW_PROJECT, REPAY_PROJECT } from '../../constants/loanProjects';
import { formatCurrency } from '../../utils/format';

// ส่วน "ดูข้อมูล" ของคู่ยืม-คืน — แยกออกมาจาก LoanPairCard.js เดิม (ตอนนี้เหลือแค่ส่วน
// กำหนด/แก้ไขบัญชีอยู่ที่ LoanPairSetupCard.js) เก็บไว้เฉพาะ 3 การ์ด: ภาพรวมยอดยืม-คืน,
// รายการยืม-คืนเงิน คชจ. (LoanLedgerTable), ประวัติ Payment — ตามที่ผู้ใช้ขอให้หน้าหลัก
// "ยืม-คืน" เหลือแค่นี้ (ใช้บ่อยกว่าส่วนกำหนดบัญชีมาก) ไม่มีปุ่ม "แก้ไขคู่บัญชี"/ฟอร์ม
// สร้าง-แก้ไขเลย
function SummaryStat({ label, value, color = '#0f172a', icon, helper }) {
  return (
    <Grid item xs={4}>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          height: '100%',
          borderRadius: 2,
          bgcolor: '#fff',
          border: '1px solid',
          borderColor: '#e2e8f0',
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Stack>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color, overflowWrap: 'anywhere', lineHeight: 1.35 }}>{value}</Typography>
        {helper && <Typography variant="caption" color="text.secondary">{helper}</Typography>}
      </Box>
    </Grid>
  );
}

export function LoanPairSummaryCard({ pair, customerName, onSaved }) {
  const dispatch = useDispatch();
  // ยอดยืม/คืนรวม "ตั้งแต่วันกำหนดยอดคงเหลือ" เท่านั้น (ไม่ใช่สะสมทั้งหมดแบบเดิม) — มาจาก
  // onTotalSinceCutoff callback ของ CustomerPaymentAccordionList (การ์ด "ประวัติ Payment")
  // คำนวณฝั่ง backend ด้วย dateFrom (ถ้ายังไม่เคยกำหนดยอดคงเหลือเลย backend จะ fallback
  // เป็นยอดสะสมทั้งหมดให้เอง เท่ากับ paymentHistoryBorrowTotal/paymentHistoryRepayTotal
  // ด้านล่างพอดี)
  const [borrowTotal, setBorrowTotal] = useState(0);
  const [repayTotal, setRepayTotal] = useState(0);
  // ยอดฝั่งยืม/ฝั่งคืนที่ยังไม่ "ยืนยันแล้ว" ตั้งแต่วันกำหนดยอดคงเหลือ (ดูนิยาม hybrid ใน
  // CustomerPaymentAccordionList.js) — คนละตัวกับ borrowTotal/repayTotal ด้านบน
  const [borrowUnverifiedTotal, setBorrowUnverifiedTotal] = useState(0);
  const [repayUnverifiedTotal, setRepayUnverifiedTotal] = useState(0);
  // ยอดยืม/คืนรวมแบบสะสมทั้งหมด (ไม่กรองด้วยวันกำหนดยอดคงเหลือ) มาจาก onTotal ของการ์ด
  // "ประวัติ Payment" — ใช้คำนวณ "ยอดคงเหลือที่ต้องคืน (อัตโนมัติ)" ที่ย้ายมาไว้ท้ายการ์ด
  // นั้นแทนการ์ดสรุปด้านบน (ซึ่งตอนนี้โชว์แค่ยอดตั้งแต่กำหนดยอดคงเหลือเท่านั้น)
  const [paymentHistoryBorrowTotal, setPaymentHistoryBorrowTotal] = useState(0);
  const [paymentHistoryRepayTotal, setPaymentHistoryRepayTotal] = useState(0);
  const openingBalance = Number(pair?.opening_balance || 0);
  // ตัวเลขที่กรอกตอนกด "กำหนดยอดคงเหลือ" ครั้งล่าสุด ค้างนิ่งไว้โชว์อ้างอิง
  const openingBalanceSeed = Number(pair?.opening_balance_seed || 0);
  // cutoff ของยอดคงเหลือ — ใช้เป็นเงื่อนไขแรกของสถานะ "ยืนยันแล้ว/ยังไม่ยืนยัน" และเป็น
  // ตัวกรองของยืมรวม/คืนรวมในการ์ดสรุปด้านบน (นับเฉพาะรายการตั้งแต่วันนี้เป็นต้นไป) —
  // ไม่กระทบ "ยอดคงเหลือที่ต้องคืน" ท้ายการ์ด "ประวัติ Payment" เลย (คำนวณจากยอดสะสม
  // ทั้งหมดเสมอ ไม่สนใจ cutoff นี้)
  const openingBalanceDate = pair?.opening_balance_date || null;
  // บังคับปิดถาวร — ไม่อ่านจาก pair.ocr_enabled อีกต่อไป (toggle ในหน้านี้ถูกล็อกให้กด
  // เปิดไม่ได้แล้ว) เก็บ field/endpoint ฝั่ง backend ไว้เผื่อกลับมาเปิดใช้ทีหลัง แค่ฝั่ง
  // UI ไม่เปิดทางให้สลับค่าอีก
  const ocrEnabled = false;
  // ยอดคงเหลือ 2 ค่านี้คำนวณจากคนละชุดตัวเลขกันโดยสิ้นเชิง แยกเป็นตัวแปรของตัวเองชัดเจน
  // กันสับสน/พลาดเอาไปปนกัน:
  // - currentBalance (การ์ด "ภาพรวมยอดยืม-คืน" ช่อง "ยอดคงเหลือ") = ยอดตั้งต้นที่กำหนดเอง
  //   (openingBalanceSeed) + ยืม/คืนเฉพาะตั้งแต่วันกำหนดยอดคงเหลือ (borrowTotal/repayTotal,
  //   กรองด้วย cutoff) — ผู้ใช้กำหนดค่าตั้งต้นเองได้ผ่านปุ่ม "กำหนดยอดตั้งต้น"
  // - outstanding (การ์ด "ประวัติ Payment" ท้ายการ์ด "ยอดคงเหลือที่ต้องคืน (อัตโนมัติ)")
  //   = ยืมรวม - คืนรวม สะสมทั้งหมดตั้งแต่อดีต (paymentHistoryBorrowTotal/RepayTotal,
  //   ไม่กรอง cutoff เลย) คำนวณอัตโนมัติล้วนๆ ไม่พึ่งค่าที่ผู้ใช้กำหนดเองแม้แต่นิดเดียว
  const currentBalance = openingBalanceSeed + borrowTotal - repayTotal;
  const outstanding = paymentHistoryBorrowTotal - paymentHistoryRepayTotal;

  const [openingBalanceDialogOpen, setOpeningBalanceDialogOpen] = useState(false);
  const [openingBalanceInput, setOpeningBalanceInput] = useState('');
  const [savingOpeningBalance, setSavingOpeningBalance] = useState(false);
  const [openingBalanceError, setOpeningBalanceError] = useState('');
  const [paymentExpanded, setPaymentExpanded] = useState(false);

  // ยอดคงเหลืออีกค่าหนึ่ง แยกจาก opening_balance โดยสิ้นเชิง — ไม่ auto sync
  // จาก Payment ทันที แต่มาจาก ocr_transactions (source_type LOAN_LEDGER) ที่
  // ผ่านการอัปโหลด "รูปสมุดจดยืม/คืนเงิน" แล้วกด verify แล้วเท่านั้น (ระบบ OCR
  // เดิมที่มีอยู่แล้ว ดู reconciliation.service.js#getSummary -> running_balance)
  const [ocrVerifiedBalance, setOcrVerifiedBalance] = useState(0);

  useEffect(() => {
    if (!pair) return;
    dispatch(actions.getReconciliationSummary({
      bankAccountId: pair.id,
      dateFrom: '2000-01-01',
      dateTo: new Date().toISOString().slice(0, 10),
    }))
      .then((summary) => setOcrVerifiedBalance(Number(summary.running_balance || 0)))
      .catch(() => {
        // เงียบไว้เช่นกัน — ไม่มี OCR ยืนยันเลยก็ยังเปิดหน้าได้ปกติ
      });
    // ผูกกับ ocr_opening_balance ด้วย ไม่ใช่แค่ pair?.id — เพราะกด "กำหนดยอดคงเหลือ"
    // แล้ว id ไม่เปลี่ยน ถ้าไม่ผูกตรงนี้ค่าจะค้าง (stale) ไม่ re-fetch ตามยอดใหม่
  }, [pair?.id, pair?.ocr_opening_balance]);

  const openOpeningBalanceDialog = () => {
    setOpeningBalanceInput(String(openingBalance));
    setOpeningBalanceError('');
    setOpeningBalanceDialogOpen(true);
  };

  const saveOpeningBalance = async () => {
    const value = Number(openingBalanceInput);
    if (Number.isNaN(value)) {
      setOpeningBalanceError('กรุณากรอกตัวเลข');
      return;
    }
    setSavingOpeningBalance(true);
    setOpeningBalanceError('');
    try {
      await dispatch(actions.updateBankAccount({ id: pair.id, openingBalance: value }));
      setOpeningBalanceDialogOpen(false);
      if (onSaved) onSaved();
    } catch (err) {
      setOpeningBalanceError(err.message);
    } finally {
      setSavingOpeningBalance(false);
    }
  };

  if (!pair) return null;

  return (
    <Box>
      <Box
        sx={{
          height: 'calc(100dvh - 88px)',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 1.75 }, borderRadius: 2.5, borderColor: '#e2e8f0', bgcolor: '#f8fafc', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
          <Box>
            <Typography sx={{ fontWeight: 750 }}>ภาพรวมยอดยืม-คืน</Typography>
            <Typography variant="caption" color="text.secondary">ยอดตั้งต้นและรายการหลังวันที่กำหนด</Typography>
          </Box>
          <Button size="small" variant="outlined" startIcon={<BalanceIcon />} onClick={openOpeningBalanceDialog}>กำหนดยอดตั้งต้น</Button>
        </Box>
        <Grid container spacing={1}>
          <SummaryStat
            label="ยอดคงเหลือ (ตามจริง)"
            value={`${formatCurrency(currentBalance)} บาท`}
            icon={<BalanceIcon fontSize="small" />}
            helper="ยอดตั้งต้น + ยืม − คืน"
          />
          <SummaryStat
            label="ยืมรวม (ตั้งแต่กำหนดยอดคงเหลือ)"
            value={`${formatCurrency(borrowTotal)} บาท`}
            color="#2563eb"
            icon={<BorrowIcon fontSize="small" />}
          />
          <SummaryStat
            label="คืนรวม (ตั้งแต่กำหนดยอดคงเหลือ)"
            value={`${formatCurrency(repayTotal)} บาท`}
            color="#059669"
            icon={<RepayIcon fontSize="small" />}
          />
        </Grid>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <LoanLedgerTable
          customerId={pair.customer_id}
          customerIdRepay={pair.customer_id_repay}
          label="รายการยืม-คืนเงิน คชจ."
          dateFrom={openingBalanceDate}
          openingBalance={openingBalanceSeed}
        />
      </Box>
      </Box>

      <Paper variant="outlined" sx={{ mt: 1.25, borderRadius: 2.5, borderColor: '#e2e8f0', overflow: 'hidden' }}>
        <Box
          onClick={() => setPaymentExpanded((value) => !value)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setPaymentExpanded((value) => !value); }}
          sx={{ px: { xs: 1.5, md: 2 }, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 34, height: 34, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: '#f1f5f9', color: '#475569' }}><HistoryIcon fontSize="small" /></Box>
            <Box>
              <Typography sx={{ fontWeight: 750 }}>ประวัติ Payment</Typography>
              <Typography variant="caption" color="text.secondary">
                {paymentExpanded ? 'ซ่อนรายละเอียดเพื่อลดพื้นที่' : 'กดเพื่อดูรายการฝั่งยืมและฝั่งคืน'}
              </Typography>
            </Box>
          </Stack>
          <Tooltip title={paymentExpanded ? 'ย่อ' : 'แสดง'}>
            <IconButton size="small" sx={{ transform: paymentExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}><ExpandIcon /></IconButton>
          </Tooltip>
        </Box>
        <Collapse in={paymentExpanded}>
          <Box sx={{ px: { xs: 1.5, md: 2 }, pb: 1.5, borderTop: '1px solid', borderColor: '#e2e8f0', pt: 1.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CustomerPaymentAccordionList
              customerId={pair.customer_id}
              label={`ฝั่งยืม — ${customerName(pair.customer_id)}`}
              projectName={BORROW_PROJECT.name}
              projectNumber={BORROW_PROJECT.number}
              dateFrom={openingBalanceDate}
              ocrEnabled={ocrEnabled}
              onTotal={setPaymentHistoryBorrowTotal}
              onTotalSinceCutoff={setBorrowTotal}
              onUnverifiedTotal={setBorrowUnverifiedTotal}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomerPaymentAccordionList
              customerId={pair.customer_id_repay}
              label={`ฝั่งคืน — ${customerName(pair.customer_id_repay)}`}
              projectName={REPAY_PROJECT.name}
              projectNumber={REPAY_PROJECT.number}
              dateFrom={openingBalanceDate}
              ocrEnabled={ocrEnabled}
              onTotal={setPaymentHistoryRepayTotal}
              onTotalSinceCutoff={setRepayTotal}
              onUnverifiedTotal={setRepayUnverifiedTotal}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: outstanding === 0 ? '#ecfdf5' : outstanding > 0 ? '#fef2f2' : '#fff7ed', borderRadius: 2, border: '1px solid', borderColor: outstanding === 0 ? '#a7f3d0' : outstanding > 0 ? '#fecaca' : '#fed7aa' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <PaymentIcon sx={{ fontSize: 20, color: outstanding > 0 ? '#dc2626' : outstanding < 0 ? '#ea580c' : '#059669' }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: outstanding > 0 ? '#dc2626' : outstanding < 0 ? '#f97316' : '#2e7d32',
            }}
          >
            {outstanding > 0 && `ยอดคงเหลือที่ต้องคืน (อัตโนมัติ): ${formatCurrency(outstanding)} บาท`}
            {outstanding < 0 && `คืนเกินยอดที่ยืม (อัตโนมัติ): ${formatCurrency(Math.abs(outstanding))} บาท`}
            {outstanding === 0 && 'ยืม-คืนครบแล้ว (อัตโนมัติ)'}
          </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {formatCurrency(paymentHistoryBorrowTotal)} − {formatCurrency(paymentHistoryRepayTotal)} บาท
          </Typography>
        </Box>

        {ocrEnabled && (
          <Box sx={{ mt: 1, p: 2, bgcolor: '#e8f4fd', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ยอดคงเหลือ (ยืนยันด้วย OCR แล้ว): {formatCurrency(ocrVerifiedBalance)} บาท
            </Typography>
            <Typography variant="caption" color="text.secondary">
              นับเฉพาะรายการที่อัปโหลด "รูปสมุดจดยืม/คืนเงิน" แล้วกดยืนยัน (verify) แล้วเท่านั้น — ไม่อัปเดตทันทีตามรายการ Payment ใหม่ (แยกยอดคงเหลือเป็นคนละค่ากับด้านบน)
            </Typography>
          </Box>
        )}

        {ocrEnabled && (
          <Box sx={{ mt: 1, p: 2, bgcolor: '#fff4e5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ส่วนต่างที่ยังไม่ได้ยืนยัน OCR (ฝั่งยืม): {formatCurrency(borrowUnverifiedTotal)} บาท
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
              ส่วนต่างที่ยังไม่ได้ยืนยัน OCR (ฝั่งคืน): {formatCurrency(repayUnverifiedTotal)} บาท
            </Typography>
            <Typography variant="caption" color="text.secondary">
              = ยอดรวม payment แต่ละฝั่งที่เกิดหลังวันที่กำหนดยอดคงเหลือ และยังไม่มีรูปสมุดจด
              ยืม/คืนเงินที่ verify ยืนยันรองรับ (รายการก่อนวันนั้นถือว่ายืนยันแล้วอัตโนมัติ)
              — ต้องอัปโหลด+verify OCR เพิ่มให้ครบเท่านี้ถึงจะยืนยันครบ
            </Typography>
          </Box>
        )}
          </Box>
        </Collapse>
      </Paper>

      {ocrEnabled && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>รายการที่ยังไม่ยืนยัน</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <LoanProjectExpenseList
                customerId={pair.customer_id}
                projectName={BORROW_PROJECT.name}
                projectNumber={BORROW_PROJECT.number}
                label={`ยืม — ${customerName(pair.customer_id)}`}
                dateFrom={openingBalanceDate}
                unverifiedOnly
                showOcrButton
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LoanProjectExpenseList
                customerId={pair.customer_id_repay}
                projectName={REPAY_PROJECT.name}
                projectNumber={REPAY_PROJECT.number}
                label={`คืน — ${customerName(pair.customer_id_repay)}`}
                dateFrom={openingBalanceDate}
                unverifiedOnly
                showOcrButton
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      <Dialog open={openingBalanceDialogOpen} onClose={() => setOpeningBalanceDialogOpen(false)}>
        <DialogTitle>กำหนดยอดคงเหลือ</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ยอดหนี้ยืมคงเหลือจริงในบัญชีธนาคาร ณ ตอนนี้ — ไม่กระทบ &quot;ยอดคงเหลือที่ต้องคืน&quot; ท้ายการ์ด
            ประวัติ Payment (ยังคำนวณจากรายการทั้งหมดเหมือนเดิม) แต่จะเปลี่ยนยืมรวม/คืนรวมในการ์ดสรุป
            ด้านบนให้นับเฉพาะรายการตั้งแต่วันนี้เป็นต้นไป และวันที่กดบันทึกจะกลายเป็น cutoff ของสถานะ
            &quot;ยืนยันแล้ว/ยังไม่ยืนยัน&quot; ด้วย — รายการก่อนวันนี้ถือว่ายืนยันแล้วอัตโนมัติ
          </Typography>
          {openingBalanceError && <Alert severity="error" sx={{ mb: 2 }}>{openingBalanceError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="ยอดคงเหลือ (บาท)"
            value={openingBalanceInput}
            onChange={(e) => setOpeningBalanceInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpeningBalanceDialogOpen(false)} disabled={savingOpeningBalance}>ยกเลิก</Button>
          <Button variant="contained" onClick={saveOpeningBalance} disabled={savingOpeningBalance}>
            {savingOpeningBalance ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LoanPairSummaryCard;
